import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { ApiError, type MFAEnrollmentResponse, type SessionResponse } from "../api/client";
import { useAuth } from "../app/AuthContext";
import { PasswordInput } from "../components/PasswordInput";
import { TotpQrCode } from "../components/TotpQrCode";

interface VisibleError {
  message: string;
  requestId?: string;
}

export function LoginPage() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const destination = requestedDestination(location.state);

  if (auth.loading) {
    return <div className="route-loading">正在确认登录状态…</div>;
  }
  if (recoveryCodes) {
    return (
      <AuthPageFrame>
        <RecoveryCodesPanel
          codes={recoveryCodes}
          onContinue={() => {
            setRecoveryCodes(null);
            navigate(destination, { replace: true });
          }}
        />
      </AuthPageFrame>
    );
  }
  if (auth.session?.next_action === "ready") {
    return <Navigate replace to={destination} />;
  }

  return (
    <AuthPageFrame>
      {!auth.session ? (
        <LoginForm notice={auth.notice} />
      ) : auth.session.next_action === "replace_password" ? (
        <PasswordReplacementForm session={auth.session} />
      ) : (
        <MfaForm mode={auth.session.next_action} onRecoveryCodes={setRecoveryCodes} />
      )}
    </AuthPageFrame>
  );
}

function AuthPageFrame({ children }: { children: ReactNode }) {
  return (
    <main className="login-page">
      <section className="login-intro">
        <span className="brand-mark" aria-hidden="true">
          EA
        </span>
        <span className="eyebrow">EcomAssistant 内部环境</span>
        <h1>从受控任务进入可信分析</h1>
        <p>登录与强认证全部由服务器端会话管理。完成必要的安全步骤后，才会开放业务工作台。</p>
      </section>
      {children}
    </main>
  );
}

function LoginForm({ notice }: { notice: string | null }) {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<VisibleError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
    } catch (caught) {
      setError(toVisibleError(caught, "登录暂时不可用，请稍后重试。"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form aria-busy={submitting} className="login-card" onSubmit={submit}>
      <span className="eyebrow">员工登录</span>
      <h2>进入业务工作台</h2>
      {notice && <AuthError error={{ message: notice }} />}
      <label htmlFor="username">用户名</label>
      <input
        autoComplete="username"
        id="username"
        onChange={(event) => setUsername(event.target.value)}
        required
        value={username}
      />
      <label htmlFor="password">密码</label>
      <PasswordInput
        autoComplete="current-password"
        hideLabel="隐藏密码"
        id="password"
        onChange={(event) => setPassword(event.target.value)}
        required
        showLabel="显示密码"
        value={password}
      />
      {error && <AuthError error={error} />}
      <button disabled={submitting} type="submit">
        {submitting ? "正在登录…" : "登录"}
      </button>
      <small>连续输错可能触发临时锁定；系统不会提示用户名是否存在。</small>
    </form>
  );
}

function PasswordReplacementForm({ session }: { session: SessionResponse }) {
  const { replacePassword } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<VisibleError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (newPassword !== confirmation) {
      setError({ message: "两次输入的新密码不一致。" });
      return;
    }
    setSubmitting(true);
    try {
      await replacePassword(newPassword);
    } catch (caught) {
      setError(toVisibleError(caught, "暂时无法替换密码，请稍后重试。"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form aria-busy={submitting} className="login-card" onSubmit={submit}>
      <span className="eyebrow">首次登录 · 第 1 步</span>
      <h2>替换临时密码</h2>
      <p className="auth-copy">临时密码只能用于本次安全设置。保存后，其他旧会话将立即失效。</p>
      <label htmlFor="new-password">新密码</label>
      <PasswordInput
        aria-describedby="password-requirements"
        autoComplete="new-password"
        hideLabel="隐藏新密码"
        id="new-password"
        minLength={12}
        onChange={(event) => setNewPassword(event.target.value)}
        required
        showLabel="显示新密码"
        value={newPassword}
      />
      <label htmlFor="confirm-password">再次输入新密码</label>
      <PasswordInput
        aria-describedby="password-requirements"
        autoComplete="new-password"
        hideLabel="隐藏确认密码"
        id="confirm-password"
        minLength={12}
        onChange={(event) => setConfirmation(event.target.value)}
        required
        showLabel="显示确认密码"
        value={confirmation}
      />
      <small id="password-requirements">
        至少 12 位，至少包含大小写字母、数字、符号中的三类，且不得包含用户名。
      </small>
      {error && <AuthError error={error} />}
      <div className="auth-actions">
        <button disabled={submitting} type="submit">
          {submitting ? "正在保存…" : "保存新密码"}
        </button>
        <ExitSessionButton disabled={submitting} />
      </div>
      <small>当前角色：{roleLabel(session.role)}</small>
    </form>
  );
}

function MfaForm({
  mode,
  onRecoveryCodes,
}: {
  mode: "enroll_mfa" | "verify_mfa";
  onRecoveryCodes: (codes: string[]) => void;
}) {
  const { beginMfaEnrollment, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState<MFAEnrollmentResponse | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<VisibleError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function enroll() {
    setError(null);
    setSubmitting(true);
    try {
      setEnrollment(await beginMfaEnrollment());
    } catch (caught) {
      setError(toVisibleError(caught, "暂时无法创建验证器密钥，请稍后重试。"));
    } finally {
      setSubmitting(false);
    }
  }

  async function verify(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await verifyMfa(code.trim());
      if (result.recovery_codes.length > 0) {
        onRecoveryCodes(result.recovery_codes);
      }
    } catch (caught) {
      setError(toVisibleError(caught, "暂时无法验证动态口令，请稍后重试。"));
    } finally {
      setSubmitting(false);
    }
  }

  const requiresEnrollment = mode === "enroll_mfa";
  return (
    <form aria-busy={submitting} className="login-card" onSubmit={verify}>
      <span className="eyebrow">特权账号 · 多因素认证</span>
      <h2>{requiresEnrollment ? "设置验证器" : "验证登录"}</h2>
      {requiresEnrollment && !enrollment ? (
        <>
          <p className="auth-copy">管理员和所有者必须绑定 TOTP 验证器，才能进入平台功能。</p>
          {error && <AuthError error={error} />}
          <button disabled={submitting} onClick={() => void enroll()} type="button">
            {submitting ? "正在创建…" : "开始设置验证器"}
          </button>
        </>
      ) : (
        <>
          {enrollment && <EnrollmentDetails enrollment={enrollment} />}
          {!requiresEnrollment && (
            <p className="auth-copy">输入验证器中的 6 位动态口令完成登录验证。</p>
          )}
          <label htmlFor="mfa-code">6 位动态口令</label>
          <input
            autoComplete="one-time-code"
            id="mfa-code"
            maxLength={6}
            onChange={(event) => setCode(event.target.value)}
            required
            value={code}
          />
          {error && <AuthError error={error} />}
          <button disabled={submitting} type="submit">
            {submitting ? "正在验证…" : "验证并继续"}
          </button>
          {!requiresEnrollment && (
            <button
              className="secondary-button"
              disabled={submitting}
              onClick={() => navigate("/account")}
              type="button"
            >
              丢失验证器？使用恢复码重置
            </button>
          )}
        </>
      )}
      <ExitSessionButton disabled={submitting} />
    </form>
  );
}

function EnrollmentDetails({ enrollment }: { enrollment: MFAEnrollmentResponse }) {
  return (
    <section aria-labelledby="enrollment-instructions" className="enrollment-details">
      <h3 id="enrollment-instructions">绑定腾讯身份验证器</h3>
      <ol>
        <li>在微信中打开“腾讯身份验证器”小程序。</li>
        <li>选择扫描二维码，并扫描下方注册二维码。</li>
        <li>输入小程序生成的 6 位动态口令完成绑定。</li>
      </ol>
      <figure className="totp-qr-figure">
        <TotpQrCode provisioningUri={enrollment.provisioning_uri} />
        <figcaption>二维码包含账号验证密钥，请勿截图、拍照或转发。</figcaption>
      </figure>
      <details className="manual-secret">
        <summary>无法扫码？手动输入密钥</summary>
        <code aria-label="TOTP 共享密钥">{enrollment.secret}</code>
      </details>
    </section>
  );
}

function RecoveryCodesPanel({ codes, onContinue }: { codes: string[]; onContinue: () => void }) {
  const [copyStatus, setCopyStatus] = useState<string | null>(null);

  async function copyCodes() {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopyStatus("恢复码已复制，请保存到安全位置。");
    } catch {
      setCopyStatus("浏览器无法复制，请手动抄录这些恢复码。");
    }
  }

  return (
    <section className="login-card recovery-card">
      <span className="eyebrow">特权账号 · 最后一步</span>
      <h2>保存一次性恢复码</h2>
      <div className="alert warning" role="alert">
        这些恢复码只显示一次。每个恢复码只能使用一次，请勿保存在共享设备或聊天工具中。
      </div>
      <ol aria-label="一次性恢复码" className="recovery-code-grid">
        {codes.map((code) => (
          <li key={code}>
            <code>{code}</code>
          </li>
        ))}
      </ol>
      {copyStatus && (
        <p className="copy-status" role="status">
          {copyStatus}
        </p>
      )}
      <div className="auth-actions">
        <button className="secondary-button" onClick={() => void copyCodes()} type="button">
          复制全部
        </button>
        <button onClick={onContinue} type="button">
          我已安全保存，进入工作台
        </button>
      </div>
    </section>
  );
}

function AuthError({ error }: { error: VisibleError }) {
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => errorRef.current?.focus(), [error.message, error.requestId]);
  return (
    <div className="alert error" ref={errorRef} role="alert" tabIndex={-1}>
      <strong>{error.message}</strong>
      {error.requestId && <small>请求编号：{error.requestId}</small>}
    </div>
  );
}

function ExitSessionButton({ disabled }: { disabled: boolean }) {
  const { logout } = useAuth();
  const [error, setError] = useState<VisibleError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function exitSession() {
    setError(null);
    setSubmitting(true);
    try {
      await logout();
    } catch (caught) {
      setError(toVisibleError(caught, "暂时无法安全退出，请稍后重试。"));
      setSubmitting(false);
    }
  }

  return (
    <>
      <button
        className="secondary-button"
        disabled={disabled || submitting}
        onClick={() => void exitSession()}
        type="button"
      >
        {submitting ? "正在退出…" : "退出此账号"}
      </button>
      {error && <AuthError error={error} />}
    </>
  );
}

function toVisibleError(caught: unknown, fallback: string): VisibleError {
  if (!(caught instanceof ApiError)) {
    return { message: fallback };
  }
  const messages: Record<string, string> = {
    invalid_credentials: "用户名或密码无效，账号也可能已被临时锁定。",
    password_policy_failed: "新密码未满足安全策略，请按页面提示重新设置。",
    mfa_invalid: "动态口令无效，请确认后重试。",
    csrf_validation_failed: "请求安全校验失败，请刷新页面后重试。",
    authentication_required: "登录会话已过期，请重新登录后继续。",
  };
  return {
    message: (caught.code && messages[caught.code]) || caught.message || fallback,
    requestId: caught.requestId,
  };
}

function requestedDestination(state: unknown): string {
  const candidate = (state as { from?: unknown } | null)?.from;
  return typeof candidate === "string" && candidate.startsWith("/") && !candidate.startsWith("//")
    ? candidate
    : "/";
}

function roleLabel(role: SessionResponse["role"]): string {
  return { user: "普通用户", manager: "管理员", owner: "所有者" }[role];
}
